import {
  Avatar,
  Badge,
  Box,
  Button,
  Separator,
  Flex,
  HStack,
  SimpleGrid,
  Stack,
  Switch,
  Text,
//   Tooltip,
  VStack,
//   useColorModeValue,
  Skeleton,
  SkeletonCircle,
} from "@chakra-ui/react";
import {
  FiCheckCircle,
  FiMail,
  FiPhone} from "react-icons/fi";
import EditableText from "../../theme/EditableText";

// Shared "card" styling for consistent polish
const card = (border, surface) => ({
  bg: surface,
  borderWidth: "1px",
  borderColor: border,
  rounded: "lg",
  p: 4,
  shadow: "sm",
});

// Tier → colorScheme mapping (override in theme if needed)

export function UserInfo({
  user,
  isLoading = false,
  onEditProfile,
  onManageAddresses,
  onManagePayments,
  onSecuritySettings,
  onManagePreferences,
}) {
  const surface = "white";
  const border = "gray.200";
  const muted = "gray.600";
  const subtle = "gray.500";

  const u = user || {};

  return (
    <Stack gapX={3} pl={2}>
      <Box {...card(border, surface)}>
        {isLoading ? (
          <HeaderSkeleton />
        ) : (
          <Flex direction={{ base: "column"}} align={{ base: "left" }} gapY={0} p={0}>
            <HStack align="center" gapY={0}>
              <Avatar.Root size="lg">
                <Avatar.Fallback name={u.first_name ?? ""} />
                <Avatar.Image src={u.avatarUrl} />
              </Avatar.Root>
              <VStack align="start" gapY={0}>
<EditableText
  m="0"
  p="0"
  value={u.first_name ?? " "}
  fontSize="lg"
  fontWeight="semibold"
  onValueChange={(e) => onEditProfile('first_name',e.value)}
/>                <Text fontSize="sm" color={subtle} mb={0}>
                  Member since {new Date(u.created_at).toDateString()}
                </Text>
              </VStack>
            </HStack>
          </Flex>
        )}
        <Separator my={4} />
        <Flex direction={{ base: "column"}} align={{ base: "left" }} gap={1} maxWidth={"350px"}>
          <HStack color={muted}>
            <FiMail color="var(--cr-yellow-3)" />
            <EditableText m="0" p="0" value={u.email ?? ""} as="span" fontSize="sm" onValueChange={(e) => onEditProfile('email',e.value)} />
            {u.is_email_verified && (
              <Badge bg={"var(--cr-green-1)"} color={"white"} variant="subtle" px={2} py={0}>
                <FiCheckCircle/> Verified
              </Badge>
              // <Tooltip content="Email verified">
              //   <FiCheckCircle color="var(--cr-green-1)" />
              // </Tooltip>
            )}
          </HStack>
          {(
            <HStack color={muted}>
              <FiPhone color="var(--cr-red-logo)" />
              <EditableText m="0" p="0" value={u.phone ?? ""} as="span" fontSize="sm" onValueChange={(e) => onEditProfile('phone',e.value)} />
              {u.is_phone_verified && (
                <Badge bg={"var(--cr-green-1)"} color={"white"} variant="subtle" px={2} py={0}>
                  <FiCheckCircle/> Verified
                </Badge>
              )}
            </HStack>
          )}
        </Flex>

        <Separator my={4} />

        {/* Stats */}
        {isLoading ? (
          <StatsSkeleton />
        ) : (
          <SimpleGrid columns={{ base: 2, md: 4 }} gap={3}>
            <StatTile label="Total orders" value={u.stats?.total_orders ?? 0} />
            <StatTile label="Total spend" value={u.stats?.total_spend ?? "-"} helper={"₹"} />
            <StatTile label="Last order" value={u.stats?.last_order_date ? new Date(u.stats?.last_order_date).toDateString() : "-"} />
            <StatTile label="Avg order value" value={u.stats?.avg_order_value ? u.stats?.avg_order_value.toFixed(2) : "-"} helper={"₹"} />
          </SimpleGrid>
        )}
      </Box>

      <SimpleGrid columns={{ base: 1, lg: 2 }} gap={2} alignItems="start">
        <Box {...card(border, surface)} height={285} 
>
          <SectionHeader
            title="Addresses"
            ctaLabel="Manage"
            onClick={onManageAddresses}
            description="Default address is used for faster checkout."
/>
          {isLoading ? (
            <ListSkeleton rows={2} />
          ) : u.addresses?.length ? (
            <Stack spacing={3}>
              {u.addresses.filter(addr => addr.is_default).map((addr, idx) => (
                <AddressRow key={addr.id ?? idx} address={addr} />
              ))}
            </Stack>
          ) : (
            <EmptyState
              title="No addresses yet"
              body="Add a shipping address to speed up checkout."
              actionLabel="Manage addresses"
              onAction={onManageAddresses}
            />
          )}
        </Box>

        {/* Payment Methods */}
        {/* <Box {...card(border, surface)}>
          <SectionHeader
            title="Payment methods"
            ctaLabel="Manage"
            onClick={onManagePayments}
            description="We store tokens, not full card numbers."
          />
          {isLoading ? (
            <ListSkeleton rows={2} />
          ) : u.paymentMethods?.length ? (
            <Stack spacing={3}>
              {u.paymentMethods.map((pm, idx) => (
                <PaymentRow key={pm.id ?? idx} method={pm} />
              ))}
            </Stack>
          ) : (
            <EmptyState
              title="No saved payments"
              body="Save a card or UPI ID for quicker checkout."
              actionLabel="Manage payments"
              onAction={onManagePayments}
            />
          )}
        </Box> */}

        {/* Preferences */}
        <Box {...card(border, surface)}>
          <SectionHeader
            title="Preferences"
            // ctaLabel="Manage"
            // onClick={onManagePreferences}
            description="Control notifications and personalization."
          />
          {isLoading ? (
            <ListSkeleton rows={1} />
          ) : (
            <Stack spacing={3}>
              <PreferenceToggle
  label="Email newsletters"
  isChecked={!!u.notification_settings?.email_notifications}
  helper="Product updates and exclusive offers."
  onChange={(checked) =>
    onManagePreferences?.("email_notifications", checked)
  }
/>
<PreferenceToggle
  label="SMS notifications"
  isChecked={!!u.notification_settings?.sms_notifications}
  helper="Delivery and order status alerts."
  onChange={(checked) =>
    onManagePreferences?.("sms_notifications", checked)
  }
/>
            </Stack>
          )}
        </Box>

        {/* Security */}
        {/* <Box {...card(border, surface)}>
          <SectionHeader title="Security" ctaLabel="Manage" onClick={onSecuritySettings} description="Keep your account protected." />
          {isLoading ? (
            <ListSkeleton rows={1} />
          ) : (
            <Stack spacing={3}>
              <HStack justify="space-between" align="start">
                <VStack align="start" spacing={0}>
                  <Text fontWeight="medium">Two‑factor authentication</Text>
                  <Text color={muted} fontSize="sm">
                    Adds an extra layer of protection to your account.
                  </Text>
                </VStack>
                <Badge colorScheme={u.security?.twoFactorEnabled ? "green" : "gray"}>
                  {u.security?.twoFactorEnabled ? "Enabled" : "Disabled"}
                </Badge>
              </HStack>
              <HStack justify="space-between">
                <Text color={muted} fontSize="sm">Last login</Text>
                <Text fontSize="sm">{u.security?.lastLogin ?? "-"}</Text>
              </HStack>
            </Stack>
          )}
        </Box> */}
      </SimpleGrid>
      
    </Stack>
  );
}

// ——— Subcomponents

function SectionHeader({ title, description, ctaLabel, onClick }) {
  return (
    <Flex justify="space-between" align={{ base: "flex-start", sm: "center" }} mb={3} gap={3}>
      <VStack align="start" spacing={0}>
        <Text as="h3" fontWeight="semibold" fontSize="lg">
          {title}
        </Text>
        {description && (
          <Text fontSize="sm" color="gray.500">
            {description}
          </Text>
        )}
      </VStack>
      {ctaLabel && (
        <Button size="sm" variant="outline" onClick={onClick}>
          {ctaLabel}
        </Button>
      )}
    </Flex>
  );
}

function StatTile({ label, value, helper }) {
  return (
    <Box>
      <Text fontSize="sm" color="gray.500">
        {label}
      </Text>
      <HStack spacing={2} align="baseline">
        <Text fontSize="xl" fontWeight="semibold">
          {value}
        </Text>
        {helper && <Text fontSize="sm" color="gray.500">{helper}</Text>}
      </HStack>
    </Box>
  );
}

function AddressRow({ address }) {
  const isDefault = address.isDefault;
  return (
    <Box borderWidth="1px" borderColor="gray.200" _dark={{ borderColor: "gray.700" }} rounded="md" p={3}>
      <HStack justify="space-between" align="start">
        <VStack align="start" spacing={0.5}>
          <Text fontWeight="medium">
            {address.name} {isDefault && <Badge ml={2} colorScheme="blue">Default</Badge>}
          </Text>
          <Text color="gray.600" _dark={{ color: "gray.300" }} fontSize="sm" noOfLines={2}>
            {address.address_line1}, {address.address_line2 ? `${address.address_line2}, ` : ""}{address.city}, {address.state} {address.postal_code}
          </Text>
        </VStack>
        <Text color="gray.500" fontSize="sm">{address.phone}</Text>
      </HStack>
    </Box>
  );
}

function PaymentRow({ method }) {
  return (
    <Box borderWidth="1px" borderColor="gray.200" _dark={{ borderColor: "gray.700" }} rounded="md" p={3}>
      <HStack justify="space-between" align="center">
        <HStack>
          <Text fontWeight="medium">
            {method.brand} •••• {method.last4}
          </Text>
          {method.isDefault && <Badge colorScheme="blue">Default</Badge>}
        </HStack>
        <Text color="gray.500" fontSize="sm">Exp {method.expMonth}/{method.expYear}</Text>
      </HStack>
      {method.type === "upi" && (
        <Text color="gray.500" fontSize="sm">UPI: {method.vpa}</Text>
      )}
    </Box>
  );
}

function PreferenceToggle({ label, helper, isChecked, onChange }) {
  return (
    <HStack justify="space-between" align="start">
      <VStack align="start" spacing={0}>
        <Text fontWeight="medium">{label}</Text>
        {helper && <Text color="gray.500" fontSize="sm">{helper}</Text>}
      </VStack>
      <Switch.Root checked={isChecked} onCheckedChange={onChange}>
        <Switch.HiddenInput />
        <Switch.Control />
        <Switch.Label></Switch.Label>
        </Switch.Root>
    </HStack>
  );
}

// ——— Skeletons

function HeaderSkeleton() {
  return (
    <HStack spacing={4}>
      <SkeletonCircle size="12" />
      <VStack align="start" spacing={2} flex="1">
        <Skeleton height="20px" width="180px" />
        <Skeleton height="16px" width="260px" />
        <Skeleton height="14px" width="140px" />
      </VStack>
      <HStack>
        <Skeleton height="32px" width="100px" />
        <Skeleton height="32px" width="36px" />
        <Skeleton height="32px" width="36px" />
      </HStack>
    </HStack>
  );
}

function StatsSkeleton() {
  return (
    <SimpleGrid columns={{ base: 2, md: 4 }} gap={3}>
      {[...Array(4)].map((_, i) => (
        <VStack key={i} align="start">
          <Skeleton height="14px" width="90px" />
          <Skeleton height="20px" width="80px" />
        </VStack>
      ))}
    </SimpleGrid>
  );
}

function ListSkeleton({ rows = 2 }) {
  return (
    <Stack spacing={3}>
      {[...Array(rows)].map((_, i) => (
        <Skeleton key={i} height="64px" />
      ))}
    </Stack>
  );
}

function EmptyState({ title, body, actionLabel, onAction }) {
  return (
    <VStack spacing={2} align="start" p={4} borderWidth="1px" borderColor="gray.200" _dark={{ borderColor: "gray.700" }} rounded="md">
      <Text fontWeight="medium">{title}</Text>
      <Text fontSize="sm" color="gray.500">{body}</Text>
      {actionLabel && (
        <Button size="sm" onClick={onAction} variant="outline">
          {actionLabel}
        </Button>
      )}
    </VStack>
  );
}